

import React, { useState, useEffect } from 'react';
import { getAllMenuItems, updateDishApproval } from "../../../Servicefile/Menuapiservice";
// ============================================
// 1. FILTERS COMPONENT
// ============================================
const ApprovalFilters = ({ filters, setFilters, vendors, categories, statuses }) => {
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div style={styles.filtersContainer}>
      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>Vendor</label>
        <select
          value={filters.vendor}
          onChange={(e) => handleFilterChange('vendor', e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">All Vendors</option>
          {vendors.map(vendor => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.logo} {vendor.name} - {vendor.city}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>Category</label>
        <select
          value={filters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>Status</label>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          style={styles.filterSelect}
        >
          {statuses.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>Date From</label>
        <input
          type="date"
          value={filters.dateRange.start}
          onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, start: e.target.value })}
          style={styles.filterInput}
        />
      </div>

      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>Date To</label>
        <input
          type="date"
          value={filters.dateRange.end}
          onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, end: e.target.value })}
          style={styles.filterInput}
        />
      </div>

      <button
        style={styles.clearFiltersBtn}
        onClick={() => setFilters({ vendor: '', category: '', status: 'PENDING', dateRange: { start: '', end: '' } })}
      >
        Clear Filters
      </button>
    </div>
  );
};

// ============================================
// 2. REJECT MODAL COMPONENT
// ============================================
const RejectModal = ({ isOpen, onClose, onReject, itemId, isBulk }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const predefinedReasons = [
    'Image quality is poor or blurry',
    'Price seems too high or unreasonable',
    'Item description is incomplete',
    'Category mismatch',
    'Duplicate item already exists',
    'Inappropriate item name or content'
  ];

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const finalReason = reason === 'other' ? customReason : reason;

    await new Promise(resolve => setTimeout(resolve, 1000));

    if (isBulk && Array.isArray(itemId)) {
      itemId.forEach(id => onReject(id, finalReason));
    } else {
      onReject(itemId, finalReason);
    }

    setIsSubmitting(false);
    setReason('');
    setCustomReason('');
    onClose();
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>❌ Reject Item</h2>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.modalBody}>
            <p style={styles.modalDescription}>
              {isBulk
                ? `You are about to reject ${Array.isArray(itemId) ? itemId.length : 1} items. Please provide a reason for rejection.`
                : 'Please provide a reason for rejecting this menu item. This will be sent to the vendor.'}
            </p>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Rejection Reason *</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                style={styles.formSelect}
              >
                <option value="">Select a reason...</option>
                {predefinedReasons.map((r, index) => (
                  <option key={index} value={r}>{r}</option>
                ))}
                <option value="other">Other (Please specify)</option>
              </select>
            </div>

            {reason === 'other' && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Custom Reason *</label>
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Please describe the reason for rejection in detail..."
                  required
                  style={styles.formTextarea}
                  rows="4"
                />
              </div>
            )}
          </div>

          <div style={styles.modalFooter}>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              style={styles.rejectSubmitBtn}
              disabled={isSubmitting || (!reason || (reason === 'other' && !customReason))}
            >
              {isSubmitting ? 'Processing...' : 'Confirm Rejection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// 3. PREVIEW MODAL COMPONENT
// ============================================
const MenuPreviewModal = ({ isOpen, onClose, item, onApprove, onReject }) => {
  if (!isOpen || !item) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING': { class: 'status-pending', label: '⏳ Pending' },
      'APPROVED': { class: 'status-approved', label: '✅ Approved' },
      'REJECTED': { class: 'status-rejected', label: '❌ Rejected' }
    };
    return statusMap[status] || statusMap.PENDING;
  };

  const statusInfo = getStatusBadge(item.status);

  return (
    <div style={styles.previewOverlay} onClick={onClose}>
      <div style={styles.previewContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.previewHeader}>
          <h2 style={styles.previewTitle}>👁️ Menu Item Preview</h2>
          <button style={styles.previewClose} onClick={onClose}>✕</button>
        </div>

        <div style={styles.previewBody}>
          <div style={styles.previewMainImage}>
            <img src={item.imageUrl} alt={item.itemName} style={styles.previewImage} />
          </div>

          <div style={styles.previewDetails}>
            <div style={styles.previewRow}>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Item Name</label>
                <h3 style={styles.previewItemName}>{item.itemName}</h3>
              </div>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Price</label>
                <div style={styles.priceDisplay}>₹{item.price}</div>
              </div>
            </div>

            <div style={styles.previewRow}>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Category</label>
                <span style={styles.fieldValue}>{item.category}</span>
              </div>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Sub-Category</label>
                <span style={styles.fieldValue}>{item.subCategory}</span>
              </div>
            </div>

            <div style={styles.previewRow}>
              <div style={{ ...styles.previewField, gridColumn: '1 / -1' }}>
                <label style={styles.previewLabel}>Description</label>
                <p style={styles.descriptionText}>{item.description}</p>
              </div>
            </div>

            <div style={styles.previewRow}>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Vendor</label>
                <div style={styles.vendorPreview}>
                  <span style={styles.vendorEmoji}>{item.vendor.logo}</span>
                  <div>
                    <div style={styles.vendorName}>{item.vendor.name}</div>
                    <div style={styles.vendorCity}>{item.vendor.city}</div>
                  </div>
                </div>
              </div>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Status</label>
                <span style={{
                  ...styles.statusBadge,
                  ...(item.status === 'PENDING' ? styles.statusPending :
                    item.status === 'APPROVED' ? styles.statusApproved :
                      styles.statusRejected)
                }}>
                  {statusInfo.label}
                </span>
              </div>
            </div>

            <div style={styles.previewRow}>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Added On</label>
                <span style={styles.fieldValue}>{formatDate(item.createdAt)}</span>
              </div>
              <div style={styles.previewField}>
                <label style={styles.previewLabel}>Vendor ID</label>
                <span style={{ ...styles.fieldValue, ...styles.vendorId }}>{item.vendor.id}</span>
              </div>
            </div>

            {item.nutritionalInfo && (
              <div style={styles.previewRow}>
                <div style={{ ...styles.previewField, gridColumn: '1 / -1' }}>
                  <label style={styles.previewLabel}>Nutritional Information</label>
                  <div style={styles.nutritionPreview}>
                    <span style={styles.nutritionItem}>🔥 {item.nutritionalInfo.calories} cal</span>
                    <span style={styles.nutritionItem}>💪 {item.nutritionalInfo.protein}</span>
                    <span style={styles.nutritionItem}>🌾 {item.nutritionalInfo.carbs}</span>
                  </div>
                </div>
              </div>
            )}

            {item.rejectionReason && (
              <div style={styles.previewRow}>
                <div style={{ ...styles.previewField, gridColumn: '1 / -1' }}>
                  <label style={styles.previewLabel}>Rejection Reason</label>
                  <div style={styles.rejectionPreview}>
                    {item.rejectionReason}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={styles.previewFooter}>
          <button style={styles.closePreviewBtn} onClick={onClose}>
            Close
          </button>
          {item.status === 'PENDING' && (
            <>
              <button
                style={styles.approvePreviewBtn}
                onClick={() => {
                  onApprove(item.id);
                  onClose();
                }}
              >
                ✅ Approve
              </button>
              <button
                style={styles.rejectPreviewBtn}
                onClick={() => {
                  onReject(item.id);
                }}
              >
                ❌ Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// 4. MAIN TABLE COMPONENT (With Expandable View)
// ============================================
const ApprovalTable = ({ items, selectedItems, onToggleSelect, onApprove, onReject, onPreview }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleExpand = (itemId) => {
    setExpandedRow(expandedRow === itemId ? null : itemId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING': { class: 'status-pending', label: '⏳ Pending' },
      'APPROVED': { class: 'status-approved', label: '✅ Approved' },
      'REJECTED': { class: 'status-rejected', label: '❌ Rejected' }
    };
    return statusMap[status] || statusMap.PENDING;
  };

  if (items.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>📭</div>
        <h3 style={styles.emptyTitle}>No menu items found</h3>
        <p style={styles.emptyText}>Try adjusting your filters or wait for vendors to add new items</p>
      </div>
    );
  }

  return (
    <div style={styles.tableResponsive}>
      <table style={styles.approvalTable}>
        <thead>
          <tr>
            <th style={styles.colCheckbox}>
              <input
                type="checkbox"
                checked={selectedItems.length === items.length && items.length > 0}
                onChange={() => {
                  if (selectedItems.length === items.length) {
                    items.forEach(item => onToggleSelect(item.id));
                  } else {
                    items.forEach(item => {
                      if (!selectedItems.includes(item.id)) {
                        onToggleSelect(item.id);
                      }
                    });
                  }
                }}
                style={styles.checkbox}
              />
            </th>
            <th style={styles.colVendor}>Vendor</th>
            <th style={styles.colItem}>Item Details</th>
            <th style={styles.colDate}>Added On</th>
            <th style={styles.colStatus}>Status</th>
            <th style={styles.colActions}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const statusInfo = getStatusBadge(item.status);
            const isExpanded = expandedRow === item.id;

            return (
              <React.Fragment key={item.id}>
                <tr style={{
                  ...styles.tableRow,
                  ...(isExpanded ? styles.tableRowExpanded : {})
                }}>
                  <td style={styles.colCheckbox}>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => onToggleSelect(item.id)}
                      style={styles.checkbox}
                    />
                  </td>
                  <td style={styles.colVendor}>
                    <div style={styles.vendorInfo}>
                      <span style={styles.vendorLogo}>{item.vendor.logo}</span>
                      <div>
                        <div style={styles.vendorName}>{item.vendor.name}</div>
                        <div style={styles.vendorId}>ID: {item.vendor.id}</div>
                        <div style={styles.vendorCity}>{item.vendor.city}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.colItem}>
                    <div style={styles.itemInfo}>
                      <img src={item.imageUrl} alt={item.itemName} style={styles.itemImage} />
                      <div style={styles.itemDetails}>
                        <div style={styles.itemName}>{item.itemName}</div>
                        <div style={styles.itemCategory}>
                          {item.category} &gt; {item.subCategory}
                        </div>
                        <div style={styles.itemPrice}>₹{item.price}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.colDate}>{formatDate(item.createdAt)}</td>
                  <td style={styles.colStatus}>
                    <span style={{
                      ...styles.statusBadge,
                      ...(item.status === 'PENDING' ? styles.statusPending :
                        item.status === 'APPROVED' ? styles.statusApproved :
                          styles.statusRejected)
                    }}>
                      {statusInfo.label}
                    </span>
                    {item.rejectionReason && (
                      <div style={styles.rejectionReason}>
                        Reason: {item.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td style={styles.colActions}>
                    <div style={styles.actionGroup}>
                      <button
                        style={{
                          ...styles.actionBtn,
                          ...styles.expandBtn,
                        }}
                        onClick={() => toggleExpand(item.id)}
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? "Hide Menu" : "View Menu"}
                      </button>

                      {item.status === "PENDING" && (
                        <>
                          <button
                            style={{
                              ...styles.actionBtn,
                              ...styles.approveBtn,
                            }}
                            onClick={() => onApprove(item.id)}
                            title="Approve"
                          >
                            ✓ Accept
                          </button>

                          <button
                            style={{
                              ...styles.actionBtn,
                              ...styles.rejectBtn,
                            }}
                            onClick={() => onReject(item.id)}
                            title="Reject"
                          >
                            ✕ Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>

                {/* EXPANDABLE ROW */}
                {isExpanded && (
                  <tr style={styles.expandableRow}>
                    <td colSpan="6" style={styles.expandableCell}>
                      <div style={styles.expandableContent}>
                        <div style={styles.expandedGrid}>
                          <div style={styles.expandedSection}>
                            <h4 style={styles.expandedTitle}>📋 Full Menu Details</h4>
                            <div style={styles.detailGrid}>
                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Item Name:</span>
                                <span style={styles.detailValue}>{item.itemName}</span>
                              </div>
                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Category:</span>
                                <span style={styles.detailValue}>{item.category}</span>
                              </div>
                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Sub-Category:</span>
                                <span style={styles.detailValue}>{item.subCategory}</span>
                              </div>
                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Price:</span>
                                <span style={styles.detailValue}>₹{item.price}</span>
                              </div>
                              <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
                                <span style={styles.detailLabel}>Description:</span>
                                <span style={styles.detailValue}>{item.description}</span>
                              </div>
                            </div>
                          </div>


                          <div style={styles.expandedSection}>
                            <h4 style={styles.expandedTitle}>🏷️ Vendor Information</h4>

                            <div style={styles.vendorDetailGrid}>
                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Vendor ID:</span>
                                <span style={styles.detailValue}>
                                  {item.vendor.id}
                                </span>
                              </div>

                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Vendor Name:</span>
                                <span style={styles.detailValue}>
                                  {item.vendor.name}
                                </span>
                              </div>

                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Company Name:</span>
                                <span style={styles.detailValue}>
                                  {item.vendor.company}
                                </span>
                              </div>

                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>City:</span>
                                <span style={styles.detailValue}>
                                  {item.vendor.city}
                                </span>
                              </div>

                              <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Added On:</span>
                                <span style={styles.detailValue}>
                                  {item.createdAt
                                    ? formatDate(item.createdAt)
                                    : "-"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div style={{ ...styles.expandedSection, gridColumn: '1 / -1' }}>
                            <h4 style={styles.expandedTitle}>🖼️ Menu Image</h4>
                            <img
                              src={item.imageUrl}
                              alt={item.itemName}
                              style={styles.expandedImage}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================
// 5. MAIN COMPONENT - MenuApproval
// ============================================
const MenuApproval = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [rejectModal, setRejectModal] = useState({ isOpen: false, itemId: null });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, item: null });
  const [loading, setLoading] = useState(true);
  const [allDishes, setAllDishes] = useState([]);
  const [filters, setFilters] = useState({
    vendor: '',
    category: '',
    status: '',
    dateRange: { start: '', end: '' }
  });

  // Mock Data - Replace with API call
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);

        const dishData = await getAllMenuItems();

        console.log("API Response:", dishData);

        const formatted = dishData
          // Skip only top-level categories
          .filter(item => item.parentId !== 0)

          // Show only Pending and Rejected
          .filter(item =>
            item.approvalStatus === null ||
            item.approvalStatus === "PENDING" ||
            item.approvalStatus === "REJECTED"
          )

          .map(item => {

            const parent = dishData.find(
              d => d.dishId === item.parentId
            );

            let category = "";
            let subCategory = "";

            if (parent) {
              if (parent.parentId === 0) {
                // Category -> Item
                category = parent.dishName;
              } else {
                // Category -> SubCategory -> Item
                subCategory = parent.dishName;

                const mainCategory = dishData.find(
                  d => d.dishId === parent.parentId
                );

                category = mainCategory?.dishName || "";
              }
            }

            return {
              id: item.dishId,
              itemName: item.dishName,

              category,
              subCategory,

              price: Number(item.price || 0),

              imageUrl:
                item.dishImage ||
                "https://via.placeholder.com/80",

              description: item.description || "",

              status: item.approvalStatus || "PENDING",

              createdAt: item.discountEndDate,

              vendor: {
                logo: "🍽️",
                id: item.vendorId,
                name: item.vendorName || "-",
                company: item.companyName || "-",
                city: item.city || "-"
              },


              tag: item.tag || "",
              menuStatus: item.menuStatus || "",
              rejectionReason: item.rejectionReason || ""
            };
          });

        console.log("Formatted:", formatted);

        setMenuItems(formatted);
        setFilteredItems(formatted);
        setAllDishes(dishData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  // Filter logic
  useEffect(() => {
    let filtered = [...menuItems];

    // Vendor Filter
    if (filters.vendor) {
      filtered = filtered.filter(
        item => String(item.vendor?.id) === String(filters.vendor)
      );
    }

    // Category Filter
    if (filters.category) {
      filtered = filtered.filter(
        item => item.category === filters.category
      );
    }

    // Status Filter
    if (filters.status) {
      filtered = filtered.filter(
        item => item.status === filters.status
      );
    }

    // Date From
    if (filters.dateRange.start) {
      const startDate = new Date(filters.dateRange.start);

      filtered = filtered.filter(
        item => new Date(item.createdAt) >= startDate
      );
    }

    // Date To
    if (filters.dateRange.end) {
      const endDate = new Date(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter(
        item => new Date(item.createdAt) <= endDate
      );
    }

    setFilteredItems(filtered);

  }, [filters, menuItems]);
  // Handle single approval
  const handleApprove = async (itemId) => {
    try {
      const response = await updateDishApproval(
        itemId,
        "APPROVED",
        ""
      );

      console.log("Approve Response :", response);

      setMenuItems(prev =>
        prev.map(item =>
          item.id === itemId
            ? {
              ...item,
              status: "APPROVED",
              rejectionReason: ""
            }
            : item
        )
      );

      alert("Menu approved successfully.");

    } catch (error) {

      console.error("Approve Error :", error);

      if (error.response) {
        alert(
          `Approve Failed\n\nStatus : ${error.response.status}\nMessage : ${error.response.data?.message ||
          JSON.stringify(error.response.data)
          }`
        );
      } else {
        alert(error.message);
      }
    }
  };

  // Handle bulk approval
  const handleBulkApprove = async () => {

    try {

      await Promise.all(

        selectedItems.map(id =>
          updateDishApproval(
            id,
            "APPROVED",
            ""
          )
        )

      );

      setMenuItems(prev =>
        prev.map(item =>
          selectedItems.includes(item.id)
            ? {
              ...item,
              status: "APPROVED",
              rejectionReason: ""
            }
            : item
        )
      );

      setSelectedItems([]);

    } catch (error) {
      console.error(error);
    }

  };

  // Handle rejection with reason
  const handleReject = async (itemId, reason) => {

    try {

      await updateDishApproval(
        itemId,
        "REJECTED",
        reason
      );

      setMenuItems(prev =>
        prev.map(item =>
          item.id === itemId
            ? {
              ...item,
              status: "REJECTED",
              rejectionReason: reason
            }
            : item
        )
      );

      setRejectModal({
        isOpen: false,
        itemId: null
      });

    } catch (error) {
      console.error(error);
      alert("Failed to reject menu item");
    }

  };

  // Toggle selection for bulk actions
  const toggleSelect = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Get unique vendors and categories for filters
  const vendors = [
    ...new Map(
      menuItems.map(item => [
        item.vendor.id,
        {
          id: item.vendor.id,
          name: item.vendor.name,      // vendorName
          city: item.vendor.city,
          logo: item.vendor.logo,
        },
      ])
    ).values(),
  ];
  const categories = [
    ...new Set(
      menuItems
        .map(item => item.category)
        .filter(Boolean)
    )
  ];
  const statuses = ['PENDING', 'APPROVED', 'REJECTED'];

  // Handle bulk reject
  const handleBulkReject = () => {
    if (selectedItems.length > 0) {
      setRejectModal({ isOpen: true, itemId: selectedItems });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerSection}>
        <h1 style={styles.pageTitle}>
          <span style={styles.titleIcon}>📋</span>
          Menu Approval Dashboard
        </h1>
        <div style={styles.statsBadge}>
          <span style={{ ...styles.stat, ...styles.statPending }}>
            {menuItems.filter(i => i.status === 'PENDING').length} Pending
          </span>
          <span style={{ ...styles.stat, ...styles.statApproved }}>
            {
              allDishes.filter(
                item => item.approvalStatus === "APPROVED"
              ).length
            } Approved
          </span>
          <span style={{ ...styles.stat, ...styles.statRejected }}>
            {menuItems.filter(i => i.status === 'REJECTED').length} Rejected
          </span>
        </div>
      </div>

      <ApprovalFilters
        filters={filters}
        setFilters={setFilters}
        vendors={vendors}
        categories={categories}
        statuses={statuses}
      />

      {selectedItems.length > 0 && (
        <div style={styles.bulkActionsBar}>
          <span style={styles.selectedCount}>{selectedItems.length} items selected</span>
          <div style={styles.bulkActions}>
            <button style={styles.bulkApproveBtn} onClick={handleBulkApprove}>
              ✅ Approve Selected
            </button>
            <button style={styles.bulkRejectBtn} onClick={handleBulkReject}>
              ❌ Reject Selected
            </button>
            <button style={styles.clearBtn} onClick={() => setSelectedItems([])}>
              ✕ Clear
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={styles.loadingSpinner}>Loading menu items...</div>
      ) : (
        <ApprovalTable
          items={filteredItems}
          selectedItems={selectedItems}
          onToggleSelect={toggleSelect}
          onApprove={handleApprove}
          onReject={(itemId) => setRejectModal({ isOpen: true, itemId })}
          onPreview={(item) => setPreviewModal({ isOpen: true, item })}
        />
      )}

      <RejectModal
        isOpen={rejectModal.isOpen}
        onClose={() => setRejectModal({ isOpen: false, itemId: null })}
        onReject={handleReject}
        itemId={rejectModal.itemId}
        isBulk={Array.isArray(rejectModal.itemId)}
      />

      <MenuPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, item: null })}
        item={previewModal.item}
        onApprove={handleApprove}
        onReject={(itemId) => {
          setPreviewModal({ isOpen: false, item: null });
          setRejectModal({ isOpen: true, itemId });
        }}
      />
    </div>
  );
};

// ============================================
// 6. ALL STYLES
// ============================================
const styles = {
  // Container Styles
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '5px',
    // background: '#f5f7fa',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },

  // Header Styles
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '25px',
    padding: '20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  pageTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a2332',
    margin: 0
  },
  titleIcon: {
    fontSize: '32px'
  },
  statsBadge: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap'
  },
  stat: {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    background: '#f0f2f5'
  },
  statPending: {
    background: '#fff3cd',
    color: '#856404'
  },
  statApproved: {
    background: '#d4edda',
    color: '#155724'
  },
  statRejected: {
    background: '#f8d7da',
    color: '#721c24'
  },

  // Filters Styles
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    padding: '20px',
    background: 'white',
    borderRadius: '12px',
    marginBottom: '25px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    alignItems: 'flex-end'
  },
  filterGroup: {
    flex: '1',
    minWidth: '150px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057'
  },
  filterSelect: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
    width: '100%'
  },
  filterInput: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
    width: '100%'
  },
  clearFiltersBtn: {
    padding: '10px 20px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    height: '42px'
  },

  // Bulk Actions Styles
  bulkActionsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '12px 20px',
    background: 'white',
    borderRadius: '8px',
    marginBottom: '20px',
    borderLeft: '4px solid #007bff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  selectedCount: {
    fontWeight: '600',
    color: '#1a2332'
  },
  bulkActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  bulkApproveBtn: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#28a745',
    color: 'white'
  },
  bulkRejectBtn: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#dc3545',
    color: 'white'
  },
  clearBtn: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#6c757d',
    color: 'white'
  },

  // Table Styles
  tableResponsive: {
    overflowX: 'auto',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  approvalTable: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px'
  },
  colCheckbox: {
    width: '40px',
    textAlign: 'center',
    padding: '15px 12px',
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '700',
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase'
  },
  colVendor: {
    padding: '15px 12px',
    textAlign: 'left',
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '700',
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase',
    minWidth: '150px'
  },
  colItem: {
    padding: '15px 12px',
    textAlign: 'left',
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '700',
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase',
    minWidth: '200px'
  },
  colDate: {
    padding: '15px 12px',
    textAlign: 'left',
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '700',
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase'
  },
  colStatus: {
    padding: '15px 12px',
    textAlign: 'left',
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '700',
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase',
    minWidth: '120px'
  },
  colActions: {
    padding: '15px 12px',
    textAlign: 'left',
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '700',
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase',
    minWidth: '160px'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },

  // Table Row Styles
  tableRow: {
    borderBottom: '1px solid #e9ecef',
    transition: 'background 0.2s'
  },
  tableRowExpanded: {
    background: '#f0f7ff'
  },
  expandableRow: {
    background: '#f8f9fa'
  },
  expandableCell: {
    padding: '0'
  },

  // Vendor Info Styles
  vendorInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  vendorLogo: {
    fontSize: '28px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    borderRadius: '50%'
  },
  vendorName: {
    fontWeight: '600',
    color: '#1a2332'
  },
  vendorId: {
    fontSize: '11px',
    color: '#6c757d'
  },
  vendorCity: {
    fontSize: '12px',
    color: '#6c757d'
  },

  // Item Info Styles
  itemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  itemImage: {
    width: '50px',
    height: '50px',
    borderRadius: '8px',
    objectFit: 'cover',
    border: '1px solid #e9ecef'
  },
  itemDetails: {
    display: 'flex',
    flexDirection: 'column'
  },
  itemName: {
    fontWeight: '600',
    color: '#1a2332'
  },
  itemCategory: {
    fontSize: '12px',
    color: '#6c757d'
  },
  itemPrice: {
    fontWeight: '700',
    color: '#28a745',
    fontSize: '14px'
  },

  // Status Badge Styles
  statusBadge: {
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  statusPending: {
    background: '#fff3cd',
    color: '#856404'
  },
  statusApproved: {
    background: '#d4edda',
    color: '#155724'
  },
  statusRejected: {
    background: '#f8d7da',
    color: '#721c24'
  },
  rejectionReason: {
    fontSize: '11px',
    color: '#dc3545',
    marginTop: '4px',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  // Action Buttons Styles
  actionButtons: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap'
  },
  actionBtn: {
    width: '34px',
    height: '34px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s'
  },
  viewBtn: {
    background: '#17a2b8',
    color: 'white'
  },
  expandBtn: {
    background: '#6c757d',
    color: 'white'
  },
  approveBtn: {
    background: '#28a745',
    color: 'white'
  },
  rejectBtn: {
    background: '#dc3545',
    color: 'white'
  },

  // Expandable Content Styles
  expandableContent: {
    padding: '20px',
    animation: 'slideDown 0.3s ease'
  },
  expandedGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  },
  expandedSection: {
    background: 'white',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  expandedTitle: {
    margin: '0 0 12px 0',
    color: '#1a2332',
    fontSize: '15px'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  detailLabel: {
    fontSize: '12px',
    color: '#6c757d',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  detailValue: {
    fontSize: '14px',
    color: '#1a2332'
  },
  nutritionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px'
  },
  nutritionItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px',
    background: '#f8f9fa',
    borderRadius: '6px'
  },
  nutritionLabel: {
    fontSize: '12px',
    color: '#6c757d',
    fontWeight: '600'
  },
  nutritionValue: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a2332'
  },
  vendorDetailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  expandedImage: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },

  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  modalContent: {
    background: 'white',
    borderRadius: '16px',
    maxWidth: '550px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef'
  },
  modalTitle: {
    margin: 0,
    fontSize: '22px',
    color: '#1a2332'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6c757d',
    cursor: 'pointer',
    padding: '0 8px'
  },
  modalBody: {
    padding: '24px'
  },
  modalDescription: {
    color: '#495057',
    marginBottom: '20px',
    fontSize: '14px',
    lineHeight: '1.6'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '16px 24px',
    borderTop: '1px solid #e9ecef'
  },
  formGroup: {
    marginBottom: '20px'
  },
  formLabel: {
    display: 'block',
    fontWeight: '600',
    color: '#1a2332',
    marginBottom: '6px',
    fontSize: '14px'
  },
  formSelect: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px'
  },
  formTextarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '100px',
    fontFamily: 'inherit'
  },
  cancelBtn: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#6c757d',
    color: 'white'
  },
  rejectSubmitBtn: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#dc3545',
    color: 'white'
  },

  // Preview Modal Styles
  previewOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  previewContent: {
    background: 'white',
    borderRadius: '16px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef'
  },
  previewTitle: {
    margin: 0,
    fontSize: '22px',
    color: '#1a2332'
  },
  previewClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6c757d',
    cursor: 'pointer',
    padding: '0 8px'
  },
  previewBody: {
    padding: '24px'
  },
  previewMainImage: {
    width: '100%',
    marginBottom: '20px',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#f8f9fa'
  },
  previewImage: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'cover'
  },
  previewDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  previewRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  previewField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  previewLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  previewItemName: {
    margin: 0,
    fontSize: '20px',
    color: '#1a2332'
  },
  priceDisplay: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#28a745'
  },
  fieldValue: {
    fontSize: '15px',
    color: '#1a2332'
  },
  descriptionText: {
    fontSize: '14px',
    color: '#495057',
    lineHeight: '1.6',
    margin: 0
  },
  vendorPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  vendorEmoji: {
    fontSize: '32px'
  },
  nutritionPreview: {
    display: 'flex',
    gap: '20px',
    padding: '10px 0'
  },
  nutritionItem: {
    padding: '6px 14px',
    background: '#f8f9fa',
    borderRadius: '20px',
    fontWeight: '600',
    fontSize: '14px'
  },
  rejectionPreview: {
    padding: '12px',
    background: '#f8d7da',
    borderRadius: '6px',
    color: '#721c24',
    fontWeight: '500'
  },
  previewFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '16px 24px',
    borderTop: '1px solid #e9ecef'
  },
  closePreviewBtn: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#6c757d',
    color: 'white'
  },
  approvePreviewBtn: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#28a745',
    color: 'white'
  },
  rejectPreviewBtn: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#dc3545',
    color: 'white'
  },

  // Empty State Styles
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px'
  },
  emptyTitle: {
    color: '#1a2332',
    marginBottom: '10px'
  },
  emptyText: {
    color: '#6c757d'
  },

  // Loading Styles
  loadingSpinner: {
    textAlign: 'center',
    padding: '60px 20px',
    fontSize: '18px',
    color: '#6c757d'
  },

  actionGroup: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },

  actionBtn: {
    padding: "10px 18px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "600",
    transition: "0.3s ease",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
    minWidth: "70px",
  },

  expandBtn: {
    background: "#2563eb",
  },

  approveBtn: {
    background: "#16a34a",
  },

  rejectBtn: {
    background: "#dc2626",
  },

};


export default MenuApproval;